import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { useOrderFormQuantities } from '../hooks/useOrderFormQuantities.ts';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { NegativeSalesPriceHint, ZeroSalesPriceHint } from '@/shared/components/PriceInputHints.tsx';
import { UnitInput } from '@/shared/inputs/UnitInput.tsx';
import { PriceInput } from '@/shared/inputs/PriceInput.tsx';
import { TaxClassInput } from '@/shared/inputs/TaxClassInput.tsx';

export function OrderSalesMaterialFieldset() {
    const { t } = useTranslation();
    const { taxClassOptions, customerOrganization, formRules } = useAutomaticFormContext();

    const methods = useFormContext<AutomaticProductsFormValues>();
    const { register, watch, setValue } = methods;
    const { onSalesMaterialUnitChange } = useOrderFormQuantities(methods);

    const [salesMaterialPrice, salesMaterialUnit, currencyCode, showZeroSalesMaterialPrice] = watch([
        'salesMaterialPrice',
        'salesMaterialUnit',
        'currencyCode',
        'showZeroSalesMaterialPrice',
    ]);

    return (
        <Fieldset title={t('forms.automaticProductsForm.sections.salesPrice')} dataTest="order-sales-material-fieldset">
            <FieldsetBody className="@sm:grid-cols-3 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn className="@2xl:col-span-4">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unitPrice')}</FieldsetLabel>
                    <PriceInput
                        {...register('salesMaterialPrice')}
                        unit={salesMaterialUnit}
                        currencyCode={currencyCode}
                        testId="sales-material-price-input"
                    />
                </FieldsetColumn>
                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    <UnitInput
                        {...register('salesMaterialUnit')}
                        options={[...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options]}
                        disabled={formRules?.salesMaterialUnit?.disabled}
                        onUnitChange={unit => onSalesMaterialUnitChange(toAutomaticUnit(unit))}
                    />
                </FieldsetColumn>
                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.taxClass')}</FieldsetLabel>
                    <TaxClassInput
                        {...register('salesMaterialTaxClassId')}
                        options={taxClassOptions}
                        testId="sales-material-tax-class-input"
                        disabled={formRules?.salesMaterialTaxClassId?.disabled}
                    />
                </FieldsetColumn>

                {Number(salesMaterialPrice) === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroSalesPriceHint
                            orderingOrganizationName={customerOrganization.name}
                            isBillingRelevant={showZeroSalesMaterialPrice ?? false}
                            onClick={() =>
                                setValue('showZeroSalesMaterialPrice', !(showZeroSalesMaterialPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(salesMaterialPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativeSalesPriceHint orderingOrganizationName={customerOrganization.name} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
